
import React, { useContext, useMemo, useState } from "react";
import { getLocale, useIntl } from "umi";
import CardBox from "@/pages/dashboard/components/CardBox";
import BarChart from "@/pages/dashboard/components/Chart/components/BarChart";
import LineChart from "@/pages/dashboard/components/Chart/components/LineChart";
import { addChartLabel, compareTrend, xAxisHighLight } from "@/pages/dashboard/components/Chart/_config/plug";
import { useLocal, useOption } from "@/pages/dashboard/utils/customHook";
import { formatPercentNum } from "@/pages/dashboard/utils/utils";
import { barFormatterFunc } from "@/pages/dashboard/components/Chart/_config/utils";
import { FilterContext } from "@/pages/dashboard/components/PageContainer/context";
import * as _ from "lodash-es";

const formatConfig = (str, allData) => {
  if (typeof str === 'string' && str.length > 0) {
    const matches = str.match(/{{(.*?)}}/);
    if (matches && matches.length > 1) {
      const {input} = matches;
      const [key, type] = matches[1].split('|');
      const value = allData?.[key];
      if (value) {
        switch (type) {
          case 'string':
            return input?.replace(matches[0], value);
          default:
            return value;
        }
      } else {
        switch (type) {
          case 'string':
            return input?.replace(matches[0], '');
        }
      }
    }
  }

  return str;
};

const loopKeysAndValues = (obj, prefix = '', allData) => {
  for (const key in obj) {
    if (Object.hasOwnProperty.call(obj, key)) {
      if (key === 'cssStyle') {
        continue;
      }
      const value = obj[key];
      if (typeof value === 'object' && value !== null) {
        loopKeysAndValues(value, `${prefix}${key}.`, allData);
      } else {
        obj[key] = formatConfig(value, allData);
      }
    }
  }
  return obj;
};

const RetentionChart = (props: any) => {
  const { config, data } = props;
  const { formatMessage } = useIntl(); // 中英切换方法
  const { isEN } = useLocal('nightingale_comparison_analysis');
  const [iseye, setIseye] = useState(true);
  const { filterVal: formValue } = useContext(FilterContext);

  const { apiKey, dataKey, className, defaultTab, height = 340, bordered } = config;
  const { retentionData, isLoading, isError, metrics } = useMemo(() => {
    const apiData = data?.[apiKey];
    if (apiData) {
      return {
        retentionData: apiData?.data?.[dataKey],
        isLoading: apiData?.loading,
        isError: apiData?.errorMsg != '',
        metrics: data?.metrics
      };
    }
    return {
      retentionData: [],
      isLoading: false,
      isError: true,
      metrics: ''
    };
  }, [data, config]);

  const formatedConfig = useMemo(() => {
    let cloneConfig = _.cloneDeep(config);
    if (formValue && cloneConfig && data) {
      cloneConfig = loopKeysAndValues(cloneConfig, '', { ...formValue, ...data });
    }
    return cloneConfig;
  }, [config, data, formValue, isEN]);

  const title = useMemo(() => {
    return isEN ? metrics.enName : metrics.cnName;
  }, [metrics]);

  const options = useMemo(() => {
    const { metric = '30' } = formValue;
    const { legend, series } = formatedConfig.chart;
    let options = {
      chart: [] as Array<{}>,
    };
    // const legend = [{ key: statisticDate, name: statisticDate }, { key: contrastDate, name: formatMessage({ id: 'common_contrast' }) + ' ' + contrastDate }]
    if (!retentionData?.length) return options;
    const chartData: Array<{}> = [];
    // 后端接口复用宽表，会有多余指标数据返回，增加无数据判断
    let isNoData = true;
    legend.forEach((ele) => {
      const chartConfig = {
        name: ele.name,
        type: 'line',
        data: [] as Array<{}>
      };
      const dataItem: any = retentionData?.find((v: any) => v?.date === ele?.key) || {};
      for (let i = 0;i < metric;i++) {
        const val = series[i];
        chartConfig.data.push({
          x: isEN ? val.name : val.nameCn,
          y: dataItem?.metrics?.[val.key]?.value ?? ''
        });
        if (dataItem?.metrics?.[val.key]?.value) isNoData = false;
      }
      chartConfig.data.length && chartData.push(chartConfig);
    });
    if (isNoData) return options;
    options = {
      chart: chartData,
    };
    return options;
  }, [retentionData, getLocale(), formatedConfig]);

  function setOtherConfigFunc(opt) {
    opt = compareTrend(opt);
    opt = addChartLabel(opt, {
      formatter: (params: { value: number; }) => {
        const result = formatPercentNum(params?.value, 2);
        return result;
      },
      show: iseye,
      formatNum: (value: number) => {
        return formatPercentNum(value, 2);
      }
    });
    opt = xAxisHighLight(opt);
    opt.yAxis.axisLabel.formatter = (value: any) => {
      return formatPercentNum(value, 0);
    };
    return opt;
  }

  function formatterFunc(param: any) {
    return barFormatterFunc(param, { isPercent: true });
  }

  const { xAxisData, legendData, lineSeries, barSeries, isNodata } = useOption(options);

  const renderList = [
    {
      type: 'eyes',
      svgType: iseye ? 'open-eyes' : 'eyes-closed',
      content: iseye ? formatMessage({ id: 'common_show_values' }) : formatMessage({ id: 'common_hide_values' }),
      isHideCom: true,
    },
    {
      type: 'barChart',
      svgType: 'column-chart',
      content: formatMessage({ id: 'common_bar_chart' }),
      component: <BarChart
        height={height}
        legendData={xAxisData.length > 0 ? legendData : []}
        xAxisData={xAxisData || []}
        series={xAxisData.length > 0 ? barSeries : []}
        formatterFunc={formatterFunc}
        setOtherConfigFunc={setOtherConfigFunc}
      />
    },
    {
      type: 'lineChart',
      svgType: 'discount-chart',
      content: formatMessage({ id: 'common_line_chart' }),
      component: <LineChart
        height={height}
        legendData={xAxisData.length > 0 ? legendData : []}
        xAxisData={xAxisData || []}
        series={xAxisData.length > 0 ? lineSeries : []}
        formatterFunc={formatterFunc}
        setOtherConfigFunc={setOtherConfigFunc}
      />
    },
  ];

  return <CardBox
    className={className}
    height={height}
    bordered={bordered}
    titleCom={title}
    renderList={renderList}
    defaultAvtive={defaultTab}
    setCurrentDataType={(type: any) => {
      if (type === 'eyes') {
        setIseye(!iseye);
      }
    }}
    isLoading={isLoading}
    isError={isError}
    isNodata={isNodata}
  />;
};

export default RetentionChart;